import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { utils } from '@app/shared';
import {
  RequirementOption,
  TicketRequirementItem,
  TicketRevisionItem,
} from '../models/ticket.model';

@Injectable({ providedIn: 'root' })
export class TicketDevelopmentService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiUrl}/api`;

  // ─── Requisitos ──────────────────────────────────────────────────────────────

  getRequirementsByTicket(ticketId: string): Promise<TicketRequirementItem[]> {
    const params = new HttpParams().set('equal[ticket_id]', ticketId);
    return firstValueFrom(
      this.http.get<{ data: TicketRequirementItem[] }>(
        `${this.baseUrl}/ticketcenter/ticket-requirements`,
        { params }
      )
    ).then(res => res?.data ?? []);
  }

  addRequirement(ticketId: string, requirementId: string): Promise<TicketRequirementItem> {
    const user = utils.getUser();
    // A API só aceita a ID do System User (user_id na tabela users) e não o token Auth BwUser.
    // Em contextos do V3ndor esse id mapeia pelo company_group ativo no contexto.
    const systemUserId = utils.getBwUserCompanyGroup(user)?.user_id || user?.id;

    return firstValueFrom(
      this.http.post<TicketRequirementItem>(
        `${this.baseUrl}/ticketcenter/ticket-requirements`,
        {
          ticket_id: ticketId,
          requirement_id: requirementId,
          user_id: systemUserId
        }
      )
    );
  }

  deleteRequirement(id: string): Promise<unknown> {
    return firstValueFrom(
      this.http.delete(`${this.baseUrl}/ticketcenter/ticket-requirements/${id}`)
    );
  }

  searchRequirements(query: string = ''): Promise<RequirementOption[]> {
    const url = `${this.baseUrl}/praxis/search/requirements?resource=1&q=${encodeURIComponent(query)}&size=10&limit=10&orderBy[description]=asc`;
    return firstValueFrom(
      this.http.get<{ data: RequirementOption[] } | RequirementOption[]>(url)
    ).then(res => (Array.isArray(res) ? res : (res?.data ?? [])));
  }

  // ─── Revisões (ID Commit) ─────────────────────────────────────────────────────

  getRevisionsByTicket(ticketId: string): Promise<TicketRevisionItem[]> {
    const params = new HttpParams().set('equal[ticket_id]', ticketId);
    return firstValueFrom(
      this.http.get<{ data: TicketRevisionItem[] }>(
        `${this.baseUrl}/ticketcenter/ticket-revisions`,
        { params }
      )
    ).then(res => res?.data ?? []);
  }

  addRevision(
    ticketId: string,
    data: Pick<TicketRevisionItem, 'id_commit' | 'date' | 'version' | 'observation'>
  ): Promise<TicketRevisionItem> {
    const user = utils.getUser();
    const systemUserId = utils.getBwUserCompanyGroup(user)?.user_id || user?.id;

    return firstValueFrom(
      this.http.post<TicketRevisionItem>(
        `${this.baseUrl}/ticketcenter/ticket-revisions`,
        { ticket_id: ticketId, user_id: systemUserId, ...data }
      )
    );
  }

  updateRevision(
    id: string,
    data: Pick<TicketRevisionItem, 'id_commit' | 'date' | 'version' | 'observation'>
  ): Promise<TicketRevisionItem> {
    return firstValueFrom(
      this.http.patch<TicketRevisionItem>(
        `${this.baseUrl}/ticketcenter/ticket-revisions/${id}`,
        data
      )
    );
  }

  deleteRevision(id: string): Promise<unknown> {
    return firstValueFrom(
      this.http.delete(`${this.baseUrl}/ticketcenter/ticket-revisions/${id}`)
    );
  }

  // ─── Socket Helpers ───────────────────────────────────────────────────────────

  static getVersionUpdateFlags(changeType: string): { requirements: boolean; revisions: boolean } {
    return {
      requirements: changeType === 'ticket_requirements' || changeType === 'ticket_development',
      revisions: changeType === 'ticket_revisions' || changeType === 'ticket_development',
    };
  }
}
